'use client'

import { memo, useCallback, useEffect, useRef } from 'react'
import { useParams } from 'next/navigation'
import Link from 'next/link'
import styled from 'styled-components'
import { useAnimation, useInView, motion, type Variants } from 'motion/react'
import { fetchAPI } from '@/api'
import { useTranslator } from '@/hooks'
import type { TypeLang } from '@/schema'
import { mq, vw, getTransition, getP24, designGrid, createUnderline } from '@/styles'

let articleSlug = null
async function getContentSlugs(lang) {
  const res = await fetchAPI('get-content-page', { lang, content_slug: 'article' }, {
    method: 'GET',
    cache: 'force-cache',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    next: {
      revalidate: 300,
    }
  }) as Response

  if (res.status === 200) {
    const data = await res.json()
    articleSlug = data[0].slug[lang]
  }
}

export const AvantFooter = memo(() => {
  const { t } = useTranslator()
  const controls = useAnimation()
  const ref = useRef(null)
  const isInView = useInView(ref, { margin: '0px 0px -100px 0px' })
  const { lang }: { lang: TypeLang } = useParams()

  const getSlug = useCallback(() => {
    if (articleSlug) return
    getContentSlugs(lang)
  }, [lang])

  useEffect(() => {
    if(isInView) {
      controls.start('animateText')
      getSlug()
    }
  }, [controls, getSlug, isInView])

  return (
    <Section ref={ref}>
      <motion.div variants={titleVariants} animate={controls} initial={'initial'}>
        <p dangerouslySetInnerHTML={{ __html: t('project.avantfooter.contact.text') }} />
        <Link href={'/contact'} prefetch={false}>{t('project.avantfooter.contact.link')}</Link>
      </motion.div>
      <motion.div variants={titleVariants} animate={controls} initial={'initial'}>
        <p dangerouslySetInnerHTML={{ __html: t('project.avantfooter.location.text') }} />
        <Link href={'/wheretobuy'} prefetch={false}>{t('project.avantfooter.location.link')}</Link>
      </motion.div>
      <motion.div variants={titleVariants} animate={controls} initial={'initial'}>
        <p dangerouslySetInnerHTML={{ __html: t('project.avantfooter.culture.text') }} />
        <Link href={`/article/${articleSlug ?? '_news'}`} prefetch={false}>{t('project.avantfooter.culture.link')}</Link>
      </motion.div>
    </Section>
  )
})


const Section = styled.section`
  ${designGrid({})}
  grid-row-gap: ${vw(60, 'mobile')};
  background-color: var(--color-creme);
  padding: ${vw(60, 'mobile')} 0;
  width: 100%;

  ${mq.greaterThan('tablet')}{
    display: flex;
    padding: ${vw(90, 'desktop')} ${vw(40, 'desktop')};
  }

  > div {
    &:nth-child(1) { grid-area: 1 / 1 / span 1 / span 3; }
    &:nth-child(2) { grid-area: 1 / 4 / span 1 / span 3; }
    &:nth-child(3) { grid-area: 2 / 1 / span 1 / span 3; }
    &:nth-child(4) { grid-area: 2 / 4 / span 1 / span 3; }
    &:nth-child(even) { margin-left: ${vw(12, 'mobile')}; }

    ${mq.greaterThan('tablet')}{
      width: ${vw(320, 'desktop')};
      margin-right: ${vw(26, 'desktop')};
      flex-shrink: 0;

      &:nth-child(even) { margin-left: 0; }
    }
  }

  p, a {
    ${getP24()}
  }

  a {
    ${createUnderline({reverse: true, color:'var(--color-text)'})}
  }

  p {
    margin-bottom: ${vw(15, 'mobile')};

    ${mq.greaterThan('tablet')}{
      margin-bottom: ${vw(30, 'desktop')};
    }
  }
`

const titleVariants:Variants = {
  initial: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition()
    }
  },
  animateText: {
    y: '0px',
    opacity: 1,
    transition: {
      ...getTransition(),
      ease: [.32,0,.29,.99],
      duration: 1
    }
  },
  exit: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .5
    }
  },
}
